/**
 * SupplierAlternatesPanel · iter303ak-fix26 — Accepted · T40 · A15
 * ────────────────────────────────────────────────────────────────
 * Collapsed by default. Shows the accepted supplier at the top and,
 * when expanded, lists the OTHER captured quotes from Quote Sent T25
 * so the operator can switch supplier if the accepted one is
 * unavailable for the confirmed timeline.
 *
 * Switching:
 *   • Updates `accepted_safety_supplier_id` on the parent appraisal
 *   • Re-posts POST /appraisals/{id}/height-safety-line firm mode
 *     with the new supplier's numbers → replaces the H&S line total
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Users, ArrowLeftRight, CheckCircle2, ChevronDown, ChevronUp, Trophy } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const nz = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SupplierAlternatesPanel({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const appr = capturedData?.appraisal_record || {};
  const acceptedId = appr?.accepted_safety_supplier_id || capturedData?.accepted_safety_supplier_id || '';
  const quotesRaw = appr?.safety_supplier_quotes || capturedData?.safety_supplier_quotes || {};
  const types = appr?.height_safety_types || capturedData?.height_safety_types || [];

  const quotes = useMemo(
    () => Object.entries(quotesRaw)
      .filter(([, q]) => q && (q.amount || q.job_ref))
      .map(([sid, q]) => ({ supplier_id: sid, ...q })),
    [quotesRaw]
  );

  const accepted = quotes.find(q => q.supplier_id === acceptedId);
  const alternates = quotes.filter(q => q.supplier_id !== acceptedId);

  const [expanded, setExpanded] = useState(false);
  const [switching, setSwitching] = useState(null); // supplier_id
  const [switchedFrom, setSwitchedFrom] = useState(value?.switched_from || null);
  const [currentAcceptedId, setCurrentAcceptedId] = useState(acceptedId);
  const [err, setErr] = useState('');

  const switchTo = async (sid) => {
    if (!parentAppraisalId) return;
    const target = quotes.find(q => q.supplier_id === sid);
    if (!target) return;
    if (!window.confirm(`Switch the accepted supplier from "${accepted?.supplier_name || 'current'}" to "${target.supplier_name}"?\n\nThe H&S line on the quote will be updated with the new amount.`)) return;
    setSwitching(sid); setErr('');
    try {
      await api.patch(`/appraisals/${parentAppraisalId}`, {
        accepted_safety_supplier_id: sid,
      });
      await api.post(`/appraisals/${parentAppraisalId}/height-safety-line`, {
        estimate: appr?.height_safety_estimate || {},
        types,
        firm: {
          supplier_name: target.supplier_name || 'Supplier',
          job_ref: target.job_ref || '',
          amount: target.amount || 0,
          downtime_days: target.downtime_days || 0,
        },
        supplier_name: target.supplier_name || 'Supplier',
      });
      setSwitchedFrom(currentAcceptedId);
      setCurrentAcceptedId(sid);
      if (onChange) onChange({ viewed_at: new Date().toISOString(), switched_from: currentAcceptedId, switched_to: sid });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Switch failed');
    } finally {
      setSwitching(null);
    }
  };

  useEffect(() => {
    if (onChange && !value?.viewed_at) onChange({ viewed_at: new Date().toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentAccepted = quotes.find(q => q.supplier_id === currentAcceptedId) || accepted;

  return (
    <div className="space-y-4" data-testid="wf-supplier-alternates-panel">
      <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 mt-0.5 shrink-0 text-purple-700" />
          <div>
            <div className="text-sm font-semibold text-purple-900">Accepted Supplier & Alternates</div>
            <div className="text-sm mt-1 text-purple-800">
              {params.prompt || 'Your accepted supplier is shown below. If they can\'t meet your timeline, expand the panel to swap in one of the alternate quotes.'}
            </div>
          </div>
        </div>
      </div>

      {/* Accepted supplier — always visible */}
      {currentAccepted ? (
        <div className="p-3 border border-emerald-300 bg-emerald-50 rounded-lg" data-testid="wf-alt-accepted">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={14} className="text-emerald-700" />
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-800">Accepted</span>
          </div>
          <div className="text-sm font-semibold text-slate-800">{currentAccepted.supplier_name}</div>
          <div className="text-[11px] text-slate-500">{currentAccepted.email}</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-700">
            <div>Amount: <b className="font-mono">{nz(currentAccepted.amount)}</b></div>
            <div>Downtime: <b>{currentAccepted.downtime_days || '—'} d</b></div>
            <div>Ref: <span className="font-mono">{currentAccepted.job_ref || '—'}</span></div>
          </div>
          {switchedFrom && switchedFrom !== currentAcceptedId && (
            <div className="mt-2 text-[10.5px] text-purple-700 italic">
              ↔ Switched from previous accepted supplier ({quotes.find(q => q.supplier_id === switchedFrom)?.supplier_name || 'unknown'})
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted supplier found on this appraisal. Complete Quote Sent T25 first to pick a winner.
        </div>
      )}

      {/* Alternates — collapsible */}
      {alternates.length > 0 && (
        <div className="border border-purple-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between gap-3 p-3 bg-purple-50 hover:bg-purple-100 text-left transition"
            data-testid="wf-alt-toggle"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-purple-900">
              <ArrowLeftRight size={14} /> {alternates.length} alternate{alternates.length === 1 ? '' : 's'} available
            </div>
            {expanded ? <ChevronUp size={16} className="text-purple-700" /> : <ChevronDown size={16} className="text-purple-700" />}
          </button>
          {expanded && (
            <div className="p-3 space-y-2 border-t border-purple-200">
              {alternates.map(a => (
                <div key={a.supplier_id} className="p-3 border border-slate-200 bg-white rounded-lg" data-testid={`wf-alt-row-${a.supplier_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800">{a.supplier_name}</div>
                      <div className="text-[11px] text-slate-500">{a.email}</div>
                      <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-700">
                        <div>Amount: <b className="font-mono">{nz(a.amount)}</b></div>
                        <div>Downtime: <b>{a.downtime_days || '—'} d</b></div>
                        <div>Ref: <span className="font-mono">{a.job_ref || '—'}</span></div>
                      </div>
                      {a.notes && <div className="text-[10.5px] text-slate-500 italic mt-1">{a.notes}</div>}
                    </div>
                    <button
                      type="button"
                      onClick={() => switchTo(a.supplier_id)}
                      disabled={switching === a.supplier_id}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold text-purple-700 bg-white border border-purple-300 hover:bg-purple-50 disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
                      data-testid={`wf-alt-switch-${a.supplier_id}`}
                    >
                      <ArrowLeftRight size={11} /> {switching === a.supplier_id ? 'Switching…' : 'Switch to this supplier'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {alternates.length === 0 && currentAccepted && (
        <div className="p-3 border border-slate-200 rounded bg-slate-50 text-xs text-slate-600 italic">
          No alternates captured on Quote Sent T25 — accepted supplier is the only quote available.
        </div>
      )}

      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-alt-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-alt-cancel">Cancel</button>}
          <button
            onClick={onNext}
            className="px-4 py-1.5 rounded-md text-sm bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2"
            data-testid="wf-alt-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
